// src/components/charts/EnhancedAnalysisTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  AlertCircle, 
  RefreshCw, 
  Settings,
  BookOpen,
  Users,
  Info,
  Filter,
  CheckCircle2,
  Circle,
  Target,
  TrendingUp,
  Award,
  Calendar,
  Hash
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AssessmentService } from '../../services/taskService';
import { StatisticsCalculator } from '../../utils/statistics';
import GradePerformanceAnalysis from './GradePerformanceAnalysis';
import PerformanceAnalysis from './PerformanceAnalysis';

const EnhancedAnalysisTab = ({ selectedSubjectGrade }) => {
  const { userProfile, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [taskMarksData, setTaskMarksData] = useState({});
  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [filters, setFilters] = useState({
    grade: selectedSubjectGrade?.grade || '',
    subject: selectedSubjectGrade?.subject || '',
    term: 'all',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadTasksWithMarks();
  }, [userProfile, currentUser, filters]);

  useEffect(() => {
    if (selectedTasks.length > 0) {
      calculateAnalysis();
    }
  }, [selectedTasks, taskMarksData]);

  const loadTasksWithMarks = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication and profile
      if (!currentUser || !userProfile || !userProfile.profileCompleted) {
        throw new Error('Please complete your profile first');
      }

      console.log('Loading tasks with marks...');

      // Get all tasks
      const allTasks = await AssessmentService.getAssessmentTasks();
      
      // Filter tasks by current filters
      let filteredTasks = allTasks;
      
      if (filters.grade) {
        filteredTasks = filteredTasks.filter(task => task.grade === filters.grade);
      }
      
      if (filters.subject) {
        filteredTasks = filteredTasks.filter(task => task.subject === filters.subject);
      }
      
      if (filters.term && filters.term !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.term === filters.term);
      }

      // Get marks for each task and only keep tasks that have marks
      const tasksWithMarks = [];
      const marksDataMap = {};

      for (const task of filteredTasks) {
        try {
          const marks = await AssessmentService.getLearnerMarks(task.id);
          if (marks && marks.length > 0) {
            // Task has marks, include it
            tasksWithMarks.push({
              ...task,
              marksCount: marks.length,
              hasMarks: true
            });
            marksDataMap[task.id] = marks;
          }
        } catch (markError) {
          console.warn(`No marks found for task ${task.id}:`, markError);
          // Skip tasks without marks
        }
      }

      console.log(`Found ${tasksWithMarks.length} tasks with marks out of ${filteredTasks.length} total tasks`);

      setAvailableTasks(tasksWithMarks);
      setTaskMarksData(marksDataMap);

      // Auto-select all tasks with marks if none selected
      if (selectedTasks.length === 0 && tasksWithMarks.length > 0) {
        setSelectedTasks(tasksWithMarks.map(task => task.id));
      }

      // Show task selector if there are multiple tasks
      setShowTaskSelector(tasksWithMarks.length > 1);

    } catch (err) {
      console.error('Error loading tasks with marks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalysis = () => {
    try {
      const selectedTasksData = availableTasks.filter(task => selectedTasks.includes(task.id));
      const allSelectedMarks = selectedTasks.flatMap(taskId => taskMarksData[taskId] || []);

      if (allSelectedMarks.length === 0) {
        setAnalysisData(null);
        return;
      }

      // Calculate comprehensive analysis
      const analysis = {
        totalTasks: selectedTasksData.length,
        totalMarks: allSelectedMarks.length,
        totalLearners: new Set(allSelectedMarks.map(mark => `${mark.learnerSurname}_${mark.learnerName}`)).size,
        
        // Overall statistics
        overallStats: StatisticsCalculator.calculateFiveNumberSummary(
          allSelectedMarks.map(mark => mark.percentageMark)
        ),
        
        // Performance levels
        performanceLevels: StatisticsCalculator.calculatePerformanceLevels(allSelectedMarks),
        
        // Task-by-task breakdown
        taskBreakdown: selectedTasksData.map(task => {
          const taskMarks = taskMarksData[task.id] || [];
          return {
            ...task,
            marksCount: taskMarks.length,
            average: taskMarks.length > 0 ? 
              taskMarks.reduce((sum, mark) => sum + mark.percentageMark, 0) / taskMarks.length : 0,
            passRate: taskMarks.length > 0 ? 
              (taskMarks.filter(mark => mark.percentageMark >= 50).length / taskMarks.length) * 100 : 0,
            stats: taskMarks.length > 0 ? 
              StatisticsCalculator.calculateFiveNumberSummary(taskMarks.map(mark => mark.percentageMark)) : null
          };
        }),

        // Topic analysis
        topicAnalysis: calculateTopicAnalysis(allSelectedMarks),
        
        // Learner analysis
        learnerAnalysis: calculateLearnerAnalysis(allSelectedMarks)
      };

      setAnalysisData(analysis);
    } catch (error) {
      console.error('Error calculating analysis:', error);
      setError('Failed to calculate analysis data');
    }
  };

  const calculateTopicAnalysis = (marks) => {
    const topicPerformance = {};
    const topicCounts = {};

    marks.forEach(mark => {
      if (mark.topic) {
        if (!topicPerformance[mark.topic]) {
          topicPerformance[mark.topic] = 0;
          topicCounts[mark.topic] = 0;
        }
        topicPerformance[mark.topic] += mark.percentageMark;
        topicCounts[mark.topic]++;
      }
    });

    const topics = Object.keys(topicPerformance).map(topic => ({
      topic,
      average: topicPerformance[topic] / topicCounts[topic],
      count: topicCounts[topic]
    })).sort((a, b) => b.average - a.average);

    return {
      strengths: topics.slice(0, 3),
      weaknesses: topics.slice(-3).reverse(),
      all: topics
    };
  };

  const calculateLearnerAnalysis = (marks) => {
    const learnerPerformance = {};

    marks.forEach(mark => {
      const learnerKey = `${mark.learnerSurname}_${mark.learnerName}`;
      if (!learnerPerformance[learnerKey]) {
        learnerPerformance[learnerKey] = {
          surname: mark.learnerSurname,
          name: mark.learnerName,
          marks: [],
          totalMarks: 0,
          averageMark: 0
        };
      }
      learnerPerformance[learnerKey].marks.push(mark.percentageMark);
    });

    // Calculate averages
    Object.values(learnerPerformance).forEach(learner => {
      learner.totalMarks = learner.marks.length;
      learner.averageMark = learner.marks.reduce((sum, mark) => sum + mark, 0) / learner.marks.length;
    });

    const sortedLearners = Object.values(learnerPerformance)
      .sort((a, b) => b.averageMark - a.averageMark);

    return {
      topPerformers: sortedLearners.slice(0, 5),
      strugglingLearners: sortedLearners.slice(-5).reverse(),
      all: sortedLearners
    };
  };

  const handleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedTasks(availableTasks.map(task => task.id));
  };

  const handleDeselectAll = () => {
    setSelectedTasks([]);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setSelectedTasks([]); // Reset selection when filters change
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tasks with marks...</p>
          <p className="text-sm text-gray-500 mt-2">Finding tasks that have recorded learner marks</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analysis Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadTasksWithMarks}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (availableTasks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks with Marks Found</h3>
          <p className="text-gray-600 mb-2">
            No assessment tasks found with recorded learner marks for your current filters.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Create tasks and record learner marks to see analysis data.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.href = '/dashboard?tab=tasks'}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Create Tasks
            </button>
            <button
              onClick={loadTasksWithMarks}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Analysis Filters & Task Selection
          </h3>
          <button
            onClick={loadTasksWithMarks}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={filters.grade}
              onChange={(e) => handleFilterChange('grade', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Grades</option>
              {userProfile?.grades?.map(grade => (
                <option key={grade} value={grade}>Grade {grade}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              value={filters.subject}
              onChange={(e) => handleFilterChange('subject', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Subjects</option>
              {userProfile?.subjects?.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
            <select
              value={filters.term}
              onChange={(e) => handleFilterChange('term', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Terms</option>
              <option value="Term 1">Term 1</option>
              <option value="Term 2">Term 2</option>
              <option value="Term 3">Term 3</option>
              <option value="Term 4">Term 4</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tasks Found</label>
            <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700">
              {availableTasks.length} tasks with marks
            </div>
          </div>
        </div>
      </div>

      {/* Task Selection */}
      {showTaskSelector && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-gray-900">
              Select Tasks for Analysis ({selectedTasks.length}/{availableTasks.length} selected)
            </h4>
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {availableTasks.map(task => (
              <div
                key={task.id}
                onClick={() => handleTaskSelection(task.id)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  selectedTasks.includes(task.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {selectedTasks.includes(task.id) ? (
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-gray-900 truncate">{task.name}</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>{task.subject} - Grade {task.grade}</div>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {task.marksCount} marks
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {task.term || 'No term'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {selectedTasks.length > 0 && analysisData && (
        <AnalysisResults analysisData={analysisData} />
      )}

      {selectedTasks.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Tasks for Analysis</h3>
            <p className="text-gray-600">
              Choose one or more tasks from the list above to view performance analysis.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Analysis Results Component
const AnalysisResults = ({ analysisData }) => {
  if (!analysisData) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysisData.totalTasks}</div>
              <div className="text-sm text-gray-600">Tasks Analyzed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysisData.totalLearners}</div>
              <div className="text-sm text-gray-600">Learners</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Hash className="w-8 h-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysisData.totalMarks}</div>
              <div className="text-sm text-gray-600">Total Marks</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-orange-600" />
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {analysisData.overallStats.mean.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Average Mark</div>
            </div>
          </div>
        </div>
      </div>

      {/* Task Performance Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Task Performance Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Task Name</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Subject</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Grade</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Marks</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Average</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Pass Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {analysisData.taskBreakdown.map(task => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{task.name}</td>
                  <td className="px-4 py-2">{task.subject}</td>
                  <td className="px-4 py-2">Grade {task.grade}</td>
                  <td className="px-4 py-2">{task.marksCount}</td>
                  <td className="px-4 py-2">
                    <span className={`font-medium ${
                      task.average >= 70 ? 'text-green-600' :
                      task.average >= 50 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {task.average.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`font-medium ${
                      task.passRate >= 80 ? 'text-green-600' :
                      task.passRate >= 60 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {task.passRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Topic Analysis */}
      {analysisData.topicAnalysis.all.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Topics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-green-600" />
              Top Performing Topics
            </h3>
            <div className="space-y-3">
              {analysisData.topicAnalysis.strengths.map((topic, index) => (
                <div key={topic.topic} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{topic.topic}</div>
                    <div className="text-sm text-gray-600">{topic.count} marks</div>
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {topic.average.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Struggling Topics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Topics Needing Attention
            </h3>
            <div className="space-y-3">
              {analysisData.topicAnalysis.weaknesses.map((topic, index) => (
                <div key={topic.topic} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{topic.topic}</div>
                    <div className="text-sm text-gray-600">{topic.count} marks</div>
                  </div>
                  <div className="text-lg font-bold text-red-600">
                    {topic.average.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance Level Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Level Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {analysisData.performanceLevels.map(level => (
            <div key={level.level} className="text-center">
              <div className={`p-4 rounded-lg ${StatisticsCalculator.getPerformanceLevelColor(level.level)}`}>
                <div className="text-2xl font-bold">{level.count}</div>
                <div className="text-sm font-medium">Level {level.level}</div>
                <div className="text-xs">{level.percentage.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnhancedAnalysisTab;